import { useEffect, useState } from 'react'
import {
  applyDesignToDocument,
  readCachedDesign,
  writeCachedDesign,
} from '@/services/designStore'
import { DEFAULT_DESIGN, fetchDesign } from '@/services/api/design'
import type { DesignConfig } from '@/types/design'

export interface DesignInitState {
  /** True until first cache hit or network response is applied. */
  booting: boolean
  design: DesignConfig
  error: string | null
}

/**
 * Blocks first paint behind a skeleton when no cache exists.
 * Instant-reloads from `localStorage`, then re-validates against `GET /api/design`.
 */
export function useDesignInit(): DesignInitState {
  const cached = readCachedDesign()
  const [design, setDesign] = useState<DesignConfig>(cached ?? DEFAULT_DESIGN)
  const [booting, setBooting] = useState(!cached)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (cached) {
      applyDesignToDocument(cached)
    }

    let cancelled = false

    void (async () => {
      try {
        const next = await fetchDesign()
        if (cancelled) return
        writeCachedDesign(next)
        applyDesignToDocument(next)
        setDesign(next)
        setError(null)
      } catch (err) {
        if (cancelled) return
        const message =
          err instanceof Error ? err.message : 'Failed to load design'
        setError(message)
        if (!cached) {
          writeCachedDesign(DEFAULT_DESIGN)
          applyDesignToDocument(DEFAULT_DESIGN)
          setDesign(DEFAULT_DESIGN)
        }
      } finally {
        if (!cancelled) setBooting(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { booting, design, error }
}
